// AdminPosts.tsx — /admin/posts, blog/SEO cycle Task 3 (spec docs/superpowers/specs/
// 2026-08-06-blog-seo-design.md §5). `GET /api/admin/posts` (routes/admin-posts.ts) returns
// every post regardless of status via `PostsRepo.listAll` — not paginated (unlike the admin
// Bots/Workspaces lists), so no `AdminPagination` here; a blog's post count never approaches
// the volume that would need it. Row click navigates to the editor (`AdminPostEditor.tsx`),
// same idiom as AdminBots.tsx's name-link-to-detail.
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { resolvePostVisibility, type AdminPostDto } from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import { SkeletonText } from '@repo/ui/components/skeleton';
import { StatusChip } from '@repo/ui/components/status-chip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { apiFetch } from '../lib/api';
import { useNow } from '../lib/use-now';
import { useLocale } from '../locales/context';
import { formatDate as formatDateIntl, formatDateTime as formatDateTimeIntl } from '../locales/format';
import type { Locale } from '../locales';
import type { Catalog } from '../locales/en';

/** Table shape at the real 44px row height (handoff Shell Anatomy §06). */
function TableSkeleton() {
  const widths = ['w-[85%]', 'w-[65%]', 'w-[75%]', 'w-[55%]'];
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border">
      <div className="h-[34px] bg-muted" />
      {widths.map((w, i) => (
        <div key={w} className={`flex h-11 items-center px-4 ${i === 0 ? '' : 'border-t'}`}>
          <SkeletonText className={w} />
        </div>
      ))}
    </div>
  );
}

function formatDate(iso: string | null, locale: Locale): string {
  if (!iso) return '—';
  return formatDateIntl(iso, locale, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso: string, locale: Locale): string {
  return formatDateTimeIntl(iso, locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Reads BOTH columns, because `status` alone is misleading once a post can be scheduled: a
 * post held for next Tuesday is `published` in the database while no visitor can reach it.
 * `resolvePostVisibility` is the shared rule; the public queries enforce the same condition.
 */
function PostStatusChip({
  post,
  now,
  locale,
  t,
}: {
  post: AdminPostDto;
  now: Date;
  locale: Locale;
  t: Catalog;
}) {
  const visibility = resolvePostVisibility(post, now);
  if (visibility === 'scheduled') {
    return (
      <StatusChip
        state="hint"
        label={t.admin.posts.scheduledLabel({
          date: formatDateTime(post.publishedAt as string, locale),
        })}
      />
    );
  }
  return (
    <StatusChip state={visibility === 'live' ? 'ready' : 'paused'} label={visibility} />
  );
}

export function AdminPosts() {
  const navigate = useNavigate();
  const now = useNow();
  const { t, locale } = useLocale();

  const { data: posts, isPending, error } = useQuery({
    queryKey: ['admin', 'posts'],
    queryFn: () => apiFetch<AdminPostDto[]>('/api/admin/posts'),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{t.admin.posts.description}</p>
        <Button asChild size="sm">
          <Link to="/admin/posts/new">{t.admin.posts.newPostCta}</Link>
        </Button>
      </div>

      {isPending && <TableSkeleton />}
      {error && <p className="text-destructive">{error.message}</p>}

      {posts && posts.length === 0 && (
        <p className="text-muted-foreground">{t.admin.posts.emptyText}</p>
      )}

      {posts && posts.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.admin.posts.colTitle}</TableHead>
              <TableHead>{t.admin.posts.colStatus}</TableHead>
              <TableHead>{t.admin.posts.colAuthor}</TableHead>
              <TableHead>{t.admin.posts.colPublished}</TableHead>
              <TableHead>{t.admin.posts.colUpdated}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {posts.map((post) => (
              <TableRow
                key={post.id}
                className="cursor-pointer"
                onClick={() => navigate(`/admin/posts/${post.id}`)}
              >
                <TableCell className="max-w-80 truncate font-medium" title={post.title}>
                  {post.title}
                </TableCell>
                <TableCell>
                  <PostStatusChip post={post} now={now} locale={locale} t={t} />
                </TableCell>
                <TableCell>{post.author.name}</TableCell>
                <TableCell>{formatDate(post.publishedAt, locale)}</TableCell>
                <TableCell>{formatDate(post.updatedAt, locale)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
